// Lab values and monitor numerals count up when an instrument appears. Pure parsing and formatting live here;
// the animation writes text straight into the DOM so a count never re-renders React.
import { useLayoutEffect, type RefObject } from 'react';
import { prefersReducedMotion } from './env';

export interface Countable {
  prefix: string;    // '<', '>' and the like, kept as printed
  negative: boolean;
  minus: string;     // the minus sign as printed ('-' or '−')
  value: number;     // absolute value
  decimals: number;
  grouped: boolean;  // printed with thousands separators
  suffix: string;
}

const RE = /^([<>≤≥~]\s*)?([-−])?(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?(\s*[^\d.,].*)?$/;

/** A printed value that can count up ("7.15", "<5", "−6", "1,240"), or undefined ("Positive", "7.1.2"). */
export function parseCountable(text: string): Countable | undefined {
  const m = RE.exec(text.trim());
  if (!m) return undefined;
  const [, prefix = '', minus = '', int, frac = '', suffix = ''] = m;
  const value = Number(`${int.replace(/,/g, '')}${frac ? `.${frac}` : ''}`);
  if (!Number.isFinite(value)) return undefined;
  return { prefix, negative: minus !== '', minus: minus || '-', value, decimals: frac.length, grouped: int.includes(','), suffix };
}

/** The value as printed at a fraction of the way from zero (0 = start, 1 = the real value). */
export function formatCount(c: Countable, progress: number): string {
  const p = Math.min(1, Math.max(0, progress));
  const v = p >= 1 ? c.value : c.value * p;
  const fixed = c.grouped
    ? v.toLocaleString('en-GB', { minimumFractionDigits: c.decimals, maximumFractionDigits: c.decimals })
    : v.toFixed(c.decimals);
  const isZero = Number(fixed.replace(/,/g, '')) === 0;
  return `${c.prefix}${c.negative && !isZero ? c.minus : ''}${fixed}${c.suffix}`;
}

export const easeOutCubic = (t: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);

export interface CountTarget { text: string; delayMs: number }

/**
 * Counts every span in `refs` up to its target text. Starts before the first paint (so the real value never
 * flashes first), runs on one animation frame loop, and leaves the real text in place when motion is reduced
 * or the value is not a number. `runKey` restarts the count.
 */
export function useCountUps(refs: RefObject<Array<HTMLElement | null>>, targets: CountTarget[], durationMs: number, runKey: string, enabled = true): void {
  useLayoutEffect(() => {
    const els = refs.current ?? [];
    const parsed = targets.map((t) => parseCountable(t.text));
    const reduced = !enabled || prefersReducedMotion() || typeof requestAnimationFrame !== 'function';
    const put = (i: number, s: string) => { const el = els[i]; if (el && el.textContent !== s) el.textContent = s; };
    if (reduced) { targets.forEach((t, i) => put(i, t.text)); return; }
    targets.forEach((t, i) => { const c = parsed[i]; put(i, c ? formatCount(c, 0) : t.text); });
    const t0 = performance.now();
    let raf = 0;
    const frame = (now: number) => {
      let running = false;
      targets.forEach((t, i) => {
        const c = parsed[i]; if (!c) return;
        const p = (now - t0 - t.delayMs) / durationMs;
        if (p < 1) running = true;
        put(i, p <= 0 ? formatCount(c, 0) : p >= 1 ? t.text : formatCount(c, easeOutCubic(p)));
      });
      if (running) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); targets.forEach((t, i) => put(i, t.text)); };
    // targets are described by runKey; the effect restarts only when the values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey, durationMs, enabled]);
}
